import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Provider } from "react-redux";
import {
    BrowserRouter,
    createRoutesFromChildren,
    matchRoutes,
    useLocation,
    useNavigationType,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";
import * as Sentry from "@sentry/react";
import { ErrorBoundary } from "react-error-boundary";
import App from "./App";
import "./main.css";
import "./lib/pdfWorker";
import { store } from "./redux/store";
import AuthProvider from "./context/auth/AuthProvider";
import AppFallback from "./components/ErrorBoundaryFallback/AppFallback/AppFallback";
import { initClientSessionSync } from "./utils/clientSessionStore";
import { filterSentryClientNoise } from "./utils/sentryClientNoiseFilter";

Sentry.init({
    dsn: import.meta.env.VITE_PATIENT_PORTAL_SENTRY_DSN,
    ignoreErrors: ["Permission denied"],
    replaysSessionSampleRate: import.meta.env.VITE_NODE_ENV !== "dev" ? 0.1 : 1,
    replaysOnErrorSampleRate: 1.0,
    enableLogs: true,
    integrations: [
        Sentry.reactRouterV7BrowserTracingIntegration({
            useEffect: React.useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
        }),
        Sentry.replayIntegration({
            maskAllText: true,
            blockAllMedia: true,
        }),
    ],
    environment: import.meta.env.VITE_NODE_ENV,
    tracesSampleRate: 0.5,
    enabled: import.meta.env.VITE_NODE_ENV !== "dev",
    beforeSend: filterSentryClientNoise,
});

// Create a client
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnMount: import.meta.env.VITE_NODE_ENV !== "dev",
            refetchOnWindowFocus: import.meta.env.VITE_NODE_ENV !== "dev",
            refetchOnReconnect: import.meta.env.VITE_NODE_ENV !== "dev",
            retry: 1,
            // set staletime of all fetched data to 5mins
            staleTime:
                import.meta.env.VITE_NODE_ENV !== "dev"
                    ? 5 * 60 * 1000
                    : Infinity,
        },
    },
});

initClientSessionSync();

ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
        <ErrorBoundary
            fallback={<AppFallback />}
            onError={(err) => {
                Sentry.captureException(err);
            }}
        >
            <AuthProvider>
                <QueryClientProvider client={queryClient}>
                    <BrowserRouter>
                        <Provider store={store()}>
                            <App />
                            <div className="fixed bottom-0 right-4 bg-primary text-white text-xs z-[1000] p-2 rounded">
                                Need help? Email us at
                                <a
                                    href={
                                        import.meta.env.VITE_NODE_ENV ===
                                        "production"
                                            ? "mailto:billing@humantold.com"
                                            : "mailto:support@mantle.com"
                                    }
                                    className="underline text-blue-200 ml-2"
                                >
                                    {import.meta.env.VITE_NODE_ENV ===
                                    "production"
                                        ? "billing@humantold.com"
                                        : "support@mantle.com"}
                                </a>
                            </div>
                        </Provider>
                        <Toaster />
                    </BrowserRouter>
                </QueryClientProvider>
            </AuthProvider>
        </ErrorBoundary>
    </React.StrictMode>
);
